import React, { useEffect, useRef, useState } from 'react';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import debounce from 'lodash.debounce';
import grapesjsPresetNewsletter from 'grapesjs-preset-newsletter';
import grapesjsStyleGradient from 'grapesjs-style-gradient';
import './grapesJs.css';
import { GjsComponent } from './GjsCustomComponents';
import { templateAPI } from '../services/api';

const fr = require('grapesjs/locale/fr').default;
const en = require('grapesjs/locale/en').default;

export interface HtmlCss {
  onlyHtml: string;
  onlyCss: string;
  inlinedHtmlAndCss: string; // à sauvegarder dans la base de données
  inlinedInnerHtmlAndCss: string; // l'Html et le CSS sans le tag body;
}

interface GrapesJsEditorProps {
  locale: string;
  setHtmlCss?: ({ onlyHtml, onlyCss, inlinedHtmlAndCss }: HtmlCss) => void;
  predefinedInlineHtmlCss?: string;
  customComponents?: Record<string, GjsComponent[]>;
  subscriberId?: string;
  templateId?: string;
}

const getMessages = (locale: string): Record<string, any> => {
  let messages = {} as Record<string, any>;
  if (locale === 'fr') {
    messages = { fr };
  } else if (locale === 'en') {
    messages = { en };
  }
  return messages;
};

export function GrapesJsEditor({
  locale,
  setHtmlCss,
  customComponents,
  predefinedInlineHtmlCss,
  subscriberId,
  templateId,
}: GrapesJsEditorProps): JSX.Element {
  const editorRef = useRef<HTMLDivElement | null>(null);

  const [editor, setEditor] = useState<any>(null);

  useEffect(() => {
    if (!editorRef.current) return;

    // Destroy existing editor if it exists
    if (editor) {
      editor.destroy();
    }

    const gjsEditor = grapesjs.init({
      container: editorRef.current,
      fromElement: false,
      i18n: {
        messages: getMessages(locale),
      },
      storageManager: false,
      plugins: [grapesjsPresetNewsletter, grapesjsStyleGradient],
      panels: {
        defaults: [],
      },
      deviceManager: {
        devices: [
        ]
      },
      canvas: {
        styles: [
          'https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/css/bootstrap.min.css'
        ]
      }
    });

    setEditor(gjsEditor);

    // Ajout des composants initiaux au BlockManager
    Object.entries(customComponents || {}).forEach(([category, components]) => {
      const categoryId = category.toLowerCase().replace(/\s+/g, '-');
      gjsEditor.BlockManager.getCategories().add({
        id: categoryId,
        label: category,
        open: true,
      });
      components.forEach(component => {
        gjsEditor.BlockManager.add(component.id, component);
      });
    });

    // Ajout des boutons personnalisés dans la barre d'options
    gjsEditor.Panels.addButton('options', [
      {
        id: 'custom-preview',
        className: 'fa fa-eye',
        command: 'custom-preview',
      },
      {
        id: 'save-template',
        className: 'fa fa-save',
        command: 'save-template',
      },
    ]);

    gjsEditor.Commands.add('custom-preview', {
      run(editor) {
        const html = editor.runCommand('gjs-get-inlined-html');
        // Open preview in new window
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(html);
          newWindow.document.close();
        }
      }
    });

    gjsEditor.Commands.add('save-template', {
      async run(editor) {
        if (!subscriberId) {
          console.error('Subscriber ID is required to save template');
          return;
        }

        const html = editor.getHtml();
        const css = editor.getCss();
        const inlinedHtml = editor.runCommand('gjs-get-inlined-html');
        const inlinedInnerHtml = inlinedHtml
          .replace(/^.*<body[^>]*>/, '')
          .replace(/<\/body>.*$/, '');

        // Clean the template for saving (remove preview data)
        const cleanedHtml = await templateAPI.cleanTemplate(inlinedInnerHtml);

        const templateData = {
          inlinedHtmlAndCss: cleanedHtml || '',
        };

        if (templateId && subscriberId) {
          templateAPI.updateTemplate(templateId, templateData)
            .then(() => console.log('Template updated successfully'))
            .catch(err => console.error('Error updating template:', err));
        } else if (subscriberId) {
          templateAPI.createTemplate(templateData)
            .then(() => console.log('Template created successfully'))
            .catch(err => console.error('Error creating template:', err));
        }
      }
    });

    if (setHtmlCss) {
      gjsEditor.on('update', () => {
        const fullHtmlCss = gjsEditor.runCommand('gjs-get-inlined-html');
        setHtmlCss({
          onlyHtml: gjsEditor?.getHtml() || '',
          onlyCss: gjsEditor?.getCss() || '',
          inlinedHtmlAndCss: fullHtmlCss,
          inlinedInnerHtmlAndCss: fullHtmlCss
            .replace(/^.*<body[^>]*>/, '')
            .replace(/<\/body>.*$/, ''),
        });
      });
    }

    gjsEditor.Panels.removeButton('options', 'preview');
    gjsEditor.Panels.removeButton('views', 'open-tm');
    gjsEditor.Panels.removeButton('options', 'gjs-toggle-images');
    [
      'link-block',
      'quote',
      'grid-items',
      'list-items',
      'video',
      'map',
      'button',
      'divider',
      'text-sect',
    ].forEach(element => {
      gjsEditor.BlockManager.remove(element);
    });

    gjsEditor.Panels.removePanel('devices-c');

    /* fixer le footer en bas de page */
    gjsEditor.on('load', () => {

        gjsEditor.Panels.removeButton('options', 'gjs-open-import-template');
        gjsEditor.Panels.removeButton('options', 'export-template');
        gjsEditor.BlockManager.remove('sect37');
        gjsEditor.StyleManager.removeSector('decorations');

      const css = `
        #footer {
          position: fixed;
          bottom: 0;
          width:100%
        }
      `;

      const iframe = gjsEditor.Canvas.getFrameEl();
      const doc = iframe.contentDocument;
      const style = doc?.createElement('style');
      if (style) {
        style.innerHTML = css;
        doc?.head.appendChild(style);
      }
    });

    const debouncedRefresh = debounce(() => {
      gjsEditor?.refresh();
    }, 500); // 500ms de délai
    gjsEditor.on('change', debouncedRefresh);
  }, [locale, setHtmlCss, customComponents, subscriberId, templateId]);

  // Handle predefinedInlineHtmlCss changes
  useEffect(() => {
    if (editor && predefinedInlineHtmlCss) {
      editor.setComponents(predefinedInlineHtmlCss);
    } else if (editor && !predefinedInlineHtmlCss) {
      editor.setComponents(`
        <body style="font-family: Arial, 'Noto Sans', Hevletica, Sans-serif, serif; 
        font-size: 14px; line-height: 16px; width: 794px; min-height: 1123px;"></body>`);
    }
  }, [editor, predefinedInlineHtmlCss]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  return (
    <div className="gjs-editor-wrapper" style={{ flex: 2, height: '100%', position: 'relative', overflow: 'hidden' }}>
      <div ref={editorRef} style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0, overflow: 'hidden' }} />
    </div>
  );
}
